import { Alert, Box, Button, Chip, DialogContent, LinearProgress, Stack, Typography } from '@mui/material';
import { useDialog } from 'muibox';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import useSubscriberVaultMediaReleaseForms, { VaultMediaTaggedItems } from '../../hooks/useSubscriberVaultMediaReleaseForms';
import { VaultMediaResponseList } from '../../hooks/useSubscriberVaultMedias';
import DialogAdaptive from '../common/DialogAdaptive';
import OnlyFansTagCreatorsSelector from '../common/OnlyFansTagCreatorsSelector';
import StyledCardTitleBar from '../common/StyledCardTitleBar';
import StyledDialogActions from '../common/StyledDialogActions';
import {
    aggregateExistingTags,
    BulkTagItemResult,
    BulkTagSummary,
    diffMediaTags,
    nextTriState,
    resolveDesiredTags,
    summarizeBulkTagResults,
    TagRef,
    TriState,
} from './vaultBulkTagging';

type Props = {
    open: boolean;
    onClose: () => void;
    subscriber: OnlyFansSubscriber;
    /** The selected Vault media items the tags will be applied to */
    items: VaultMediaResponseList[];
    /** Called once every item was processed without failures so the caller can clear the selection */
    onCompleted?: (summary: BulkTagSummary) => void;
};

type Progress = { index: number; total: number } | null;

const EMPTY_TAGS: VaultMediaTaggedItems = { taggedCreators: [], taggedReleaseForms: [] };

const toIdSet = (refs: TagRef[]) => new Set(refs.map(ref => ref.id));

/**
 * Bulk-tag a selection of Vault media with creators and release (consent) forms.
 *
 * On open, every selected item's current tags are fetched. Tags carried by all items start checked,
 * tags carried by only some start indeterminate (left as-is unless changed). Clicking cycles a mixed
 * tag through checked -> unchecked -> indeterminate. On apply each item is updated one at a time,
 * and only when its resolved tag set differs from what it already has.
 */
const VaultBulkTagDialog = ({ open, onClose, subscriber, items, onCompleted }: Props) => {
    const dialog = useDialog();
    const { getVaultMediaTaggedItems, setVaultMediaTags } = useSubscriberVaultMediaReleaseForms();

    // Checked tags (applied to every item on apply)
    const [taggedCreators, setTaggedCreators] = useState<TagRef[]>([]);
    const [taggedReleaseForms, setTaggedReleaseForms] = useState<TagRef[]>([]);
    // Indeterminate tags (left as-is on each item on apply)
    const [partialCreatorIds, setPartialCreatorIds] = useState<Set<number>>(new Set());
    const [partialReleaseFormIds, setPartialReleaseFormIds] = useState<Set<number>>(new Set());
    // Tags that were mixed across the selection when loaded; these get the three-way click cycle
    const [mixedCreatorIds, setMixedCreatorIds] = useState<Set<number>>(new Set());
    const [mixedReleaseFormIds, setMixedReleaseFormIds] = useState<Set<number>>(new Set());

    const [existingByMedia, setExistingByMedia] = useState<Map<number, VaultMediaTaggedItems>>(new Map());
    const [loadProgress, setLoadProgress] = useState<Progress>(null);
    const [applyProgress, setApplyProgress] = useState<Progress>(null);
    const [summary, setSummary] = useState<BulkTagSummary | null>(null);

    const stopRequestedRef = useRef(false);
    // Bumped on every open / close so a fetch loop from a previous open cannot write into the new session
    const loadSessionRef = useRef(0);
    // Latest props / callbacks for the open-time load effect, which must only re-run when `open` changes
    const itemsRef = useRef(items);
    const getVaultMediaTaggedItemsRef = useRef(getVaultMediaTaggedItems);

    itemsRef.current = items;
    getVaultMediaTaggedItemsRef.current = getVaultMediaTaggedItems;

    const isLoading = loadProgress !== null;
    const isApplying = applyProgress !== null;
    const isBusy = isLoading || isApplying;

    const resetState = useCallback(() => {
        setTaggedCreators([]);
        setTaggedReleaseForms([]);
        setPartialCreatorIds(new Set());
        setPartialReleaseFormIds(new Set());
        setMixedCreatorIds(new Set());
        setMixedReleaseFormIds(new Set());
        setExistingByMedia(new Map());
        setLoadProgress(null);
        setApplyProgress(null);
        setSummary(null);
        stopRequestedRef.current = false;
    }, []);

    // Fetch each selected item's current tags when the dialog opens, then pre-tick the selector
    useEffect(() => {
        loadSessionRef.current += 1;

        if (!open) return;

        const session = loadSessionRef.current;
        const targets = itemsRef.current;
        const fetchTaggedItems = getVaultMediaTaggedItemsRef.current;

        resetState();

        const load = async () => {
            const fetched = new Map<number, VaultMediaTaggedItems>();

            for (let index = 0; index < targets.length; index += 1) {
                if (loadSessionRef.current !== session) return;

                setLoadProgress({ index, total: targets.length });

                const existing = await fetchTaggedItems(targets[index].id);

                fetched.set(targets[index].id, existing);
            }

            if (loadSessionRef.current !== session) return;

            const { all, some } = aggregateExistingTags(targets.map(item => fetched.get(item.id) ?? EMPTY_TAGS));

            setExistingByMedia(fetched);
            setTaggedCreators(all.taggedCreators);
            setTaggedReleaseForms(all.taggedReleaseForms);
            setPartialCreatorIds(toIdSet(some.taggedCreators));
            setPartialReleaseFormIds(toIdSet(some.taggedReleaseForms));
            setMixedCreatorIds(toIdSet(some.taggedCreators));
            setMixedReleaseFormIds(toIdSet(some.taggedReleaseForms));
            setLoadProgress(null);
        };

        void load();
    }, [open, resetState]);

    const handleClose = () => {
        if (isApplying) return;

        // Abandon any in-flight tag loading
        loadSessionRef.current += 1;
        onClose();
    };

    // Tri-state click cycle shared by creators and release forms
    const cycleTag = (
        item: TagRef,
        tagged: TagRef[],
        setTagged: React.Dispatch<React.SetStateAction<TagRef[]>>,
        partialIds: Set<number>,
        setPartialIds: React.Dispatch<React.SetStateAction<Set<number>>>,
        mixedIds: Set<number>,
    ) => {
        const current: TriState = tagged.some(ref => ref.id === item.id)
            ? 'checked'
            : partialIds.has(item.id)
            ? 'indeterminate'
            : 'unchecked';
        const next = nextTriState(current, mixedIds.has(item.id));

        const withoutItem = (refs: TagRef[]) => refs.filter(ref => ref.id !== item.id);
        const setWithout = (ids: Set<number>) => {
            const copy = new Set(ids);
            copy.delete(item.id);
            return copy;
        };

        if (next === 'checked') {
            setTagged(prev => [...withoutItem(prev), item]);
            setPartialIds(setWithout);
        } else if (next === 'indeterminate') {
            setTagged(withoutItem);
            setPartialIds(prev => new Set(prev).add(item.id));
        } else {
            setTagged(withoutItem);
            setPartialIds(setWithout);
        }
    };

    const handleCreatorClicked = (item: TagRef) =>
        cycleTag(item, taggedCreators, setTaggedCreators, partialCreatorIds, setPartialCreatorIds, mixedCreatorIds);
    const handleReleaseFormClicked = (item: TagRef) =>
        cycleTag(item, taggedReleaseForms, setTaggedReleaseForms, partialReleaseFormIds, setPartialReleaseFormIds, mixedReleaseFormIds);

    // A tag added through search or chips is checked, so it can no longer be indeterminate
    const indeterminateCreatorIds = useMemo(() => {
        const checked = toIdSet(taggedCreators);
        return new Set(Array.from(partialCreatorIds).filter(id => !checked.has(id)));
    }, [partialCreatorIds, taggedCreators]);
    const indeterminateReleaseFormIds = useMemo(() => {
        const checked = toIdSet(taggedReleaseForms);
        return new Set(Array.from(partialReleaseFormIds).filter(id => !checked.has(id)));
    }, [partialReleaseFormIds, taggedReleaseForms]);

    const runBulkTagging = async () => {
        const checked = { taggedCreators, taggedReleaseForms };
        const results: BulkTagItemResult[] = [];
        const updatedExisting = new Map(existingByMedia);

        stopRequestedRef.current = false;
        setSummary(null);

        for (let index = 0; index < items.length; index += 1) {
            if (stopRequestedRef.current) break;

            const item = items[index];

            setApplyProgress({ index, total: items.length });

            // Use the tags loaded on open; fall back to a fresh fetch if loading was interrupted
            const existing = updatedExisting.get(item.id) ?? (await getVaultMediaTaggedItems(item.id));
            const desired = resolveDesiredTags(existing, checked, indeterminateCreatorIds, indeterminateReleaseFormIds);
            const diff = diffMediaTags(existing, desired);

            if (!diff.needsUpdate) {
                results.push({ mediaId: item.id, status: 'unchanged', diff });
                continue;
            }

            const success = await setVaultMediaTags(
                item.id,
                desired.taggedCreators.map(ref => ref.id),
                desired.taggedReleaseForms.map(ref => ref.id),
            );

            if (success) updatedExisting.set(item.id, desired);

            results.push({ mediaId: item.id, status: success ? 'updated' : 'failed', diff });
        }

        const runSummary = summarizeBulkTagResults(results);

        setExistingByMedia(updatedExisting);
        setApplyProgress(null);
        setSummary(runSummary);

        const stoppedEarly = results.length < items.length;

        if (runSummary.failed === 0 && !stoppedEarly) {
            enqueueSnackbar(
                runSummary.updated > 0
                    ? `Tags applied to ${runSummary.updated} item${runSummary.updated === 1 ? '' : 's'}${
                          runSummary.unchanged > 0 ? ` (${runSummary.unchanged} already up to date)` : ''
                      }`
                    : 'All selected items already had these tags',
                { variant: 'success', autoHideDuration: 4000 },
            );

            onCompleted?.(runSummary);
            onClose();

            return;
        }

        // Keep the dialog open (and the selection intact) so the failures / early stop can be seen and retried
    };

    const handleApply = async () => {
        if (items.length === 0 || isBusy) return;

        const nothingKept =
            taggedCreators.length === 0 &&
            taggedReleaseForms.length === 0 &&
            indeterminateCreatorIds.size === 0 &&
            indeterminateReleaseFormIds.size === 0;

        if (nothingKept) {
            const confirmed = await dialog
                .confirm({
                    title: 'Remove All Tags?',
                    message: `No creators or release forms are selected. Applying will remove every existing tag from the ${
                        items.length
                    } selected item${items.length === 1 ? '' : 's'}. Continue?`,
                    ok: { text: 'Remove Tags', color: 'error', variant: 'contained' },
                    cancel: { text: 'Cancel', color: 'secondary', variant: 'text' },
                })
                .then(() => true)
                .catch(() => false);

            if (!confirmed) return;
        }

        await runBulkTagging();
    };

    const itemsLabel = `${items.length} Selected Media${items.length === 1 ? '' : 's'}`;
    const mixedCount = indeterminateCreatorIds.size + indeterminateReleaseFormIds.size;

    return (
        <DialogAdaptive open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <StyledCardTitleBar title={`Tag ${itemsLabel}`} />

            <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
                {!summary && !isLoading && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Checked tags will be applied to all {items.length} selected media{items.length === 1 ? '' : 's'}, unchecked tags
                        will be removed from them.
                        {mixedCount > 0 && (
                            <>
                                {' '}
                                Tags shown with a dash are only on some of the media and will be left as they are. Click one to apply it to
                                all, again to remove it from all, and again to leave it as is.
                            </>
                        )}
                    </Alert>
                )}

                {summary && (
                    <Alert severity={summary.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                            <Chip size="small" color="success" label={`${summary.updated} updated`} />
                            <Chip size="small" label={`${summary.unchanged} unchanged`} />
                            {summary.failed > 0 && <Chip size="small" color="error" label={`${summary.failed} failed`} />}
                        </Stack>
                        {summary.failed > 0 && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Failed media IDs: {summary.failedMediaIds.join(', ')}. You can try again with the same selection.
                            </Typography>
                        )}
                    </Alert>
                )}

                {loadProgress && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Loading existing tags of media {loadProgress.index + 1} of {loadProgress.total}...
                        </Typography>
                        <LinearProgress variant="determinate" value={(loadProgress.index / loadProgress.total) * 100} />
                    </Box>
                )}

                {applyProgress && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Tagging media {applyProgress.index + 1} of {applyProgress.total}...
                        </Typography>
                        <LinearProgress variant="determinate" value={(applyProgress.index / applyProgress.total) * 100} />
                    </Box>
                )}

                <OnlyFansTagCreatorsSelector
                    subscriber={subscriber}
                    tagCreators={true}
                    setTagCreators={() => {}}
                    taggedCreators={taggedCreators}
                    setTaggedCreators={setTaggedCreators}
                    taggedReleaseForms={taggedReleaseForms}
                    setTaggedReleaseForms={setTaggedReleaseForms}
                    indeterminateCreatorIds={indeterminateCreatorIds}
                    indeterminateReleaseFormIds={indeterminateReleaseFormIds}
                    onCreatorListItemClicked={handleCreatorClicked}
                    onReleaseFormListItemClicked={handleReleaseFormClicked}
                    session={null}
                    disabled={isBusy}
                />
            </DialogContent>

            <StyledDialogActions>
                {isApplying ? (
                    <Button
                        onClick={() => {
                            stopRequestedRef.current = true;
                        }}
                        variant="outlined"
                        color="warning"
                    >
                        Stop After Current Item
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} variant="outlined" color="secondary">
                            {summary ? 'Close' : 'Cancel'}
                        </Button>
                        <Button onClick={handleApply} variant="contained" color="primary" disabled={items.length === 0 || isLoading}>
                            {summary ? 'Apply Again' : `Apply to ${itemsLabel}`}
                        </Button>
                    </>
                )}
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default VaultBulkTagDialog;
